import React, { useState } from 'react'
import { Form } from 'react-bootstrap'
import { MissingScopeAction } from '../../../../components/ScopedBlock/models'
import { ScopedBlock } from '../../../../components/ScopedBlock/ScopedBlock'
import {
  useDisableWorkflowMutation,
  useEnableWorkflowMutation,
  useGetWorkflowSettingsQuery,
  useUpdateEnabledWorkflowMutation,
} from '../../../../store/workflow/enabledWorkflows'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { WORKFLOWS } from '../../../Workflows/WorkflowMapping'
import { ZocksAIAssistantTagVertical } from '../../../../components/Tag/Tag'
import { Button } from '../../../../components/Button/Button'
import { SelectDropdown } from '../../../../components/SelectDropdown/SelectDropdown'
import { EnterpriseManagedBanner } from '../EnterpriseManagedBanner'
import { useAutoMeetingPrep } from './hooks'
import { normalizePeriodValue, getPeriodType, getPeriodValue, PERIOD_OPTIONS, type PeriodType } from './utils'
import './AutoMeetingPrepConfig.scss'

const DEFAULT_LOOKAHEAD_HOURS = 20

export const AutoMeetingPrepConfig: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)

  const [enableWorkflow, { isLoading: enableLoading }] = useEnableWorkflowMutation()
  const [disableWorkflow, { isLoading: disableLoading }] = useDisableWorkflowMutation()
  const [updateEnabledWorkflow, { isLoading: updateLoading }] = useUpdateEnabledWorkflowMutation()

  const { autoMeetingPrepQuotaEnabled, autoMeetingPrepWorkflow, autoMeetingPrepFetching } = useAutoMeetingPrep()
  const { data: workflowSettings, isFetching: workflowSettingsFetching, isError: workflowSettingsError } = useGetWorkflowSettingsQuery({ accountId })

  const [newPeriodType, setNewPeriodType] = useState<PeriodType | null>(null)
  const [newPeriodValue, setNewPeriodValue] = useState<number | null>(null)

  const enterpriseEnforced = workflowSettings?.data.find((w) => w.workflowId === WORKFLOWS.meetingPrep.id)?.enterpriseEnforced ?? false
  const isLoading = autoMeetingPrepFetching || workflowSettingsFetching || enableLoading || disableLoading || updateLoading
  const currentLookaheadHours = autoMeetingPrepWorkflow?.context.lookaheadHours ?? DEFAULT_LOOKAHEAD_HOURS
  const periodType = newPeriodType ?? getPeriodType(currentLookaheadHours)
  const periodValue = newPeriodValue ?? getPeriodValue(currentLookaheadHours)
  const lookaheadHours = periodType === 'hours' ? periodValue : periodValue * 24
  const lookAheadHoursChanged = autoMeetingPrepWorkflow?.context.lookaheadHours !== lookaheadHours
  const enterpriseManagedMessage = autoMeetingPrepWorkflow
    ? "Automatic Meeting Prep generation is enforced by your enterprise admin and can't be turned off here. You can still adjust how far in advance meetings are prepared."
    : "Automatic Meeting Prep generation is disabled by your enterprise admin and can't be turned on here."

  const handleToggleAutoMeetingPrep = async (newEnabled: boolean) => {
    if (!newEnabled && autoMeetingPrepWorkflow) {
      await disableWorkflow({ accountId, enabledWorkflowId: autoMeetingPrepWorkflow.id })
    } else if (newEnabled) {
      await enableWorkflow({ accountId, payload: { workflowId: WORKFLOWS.meetingPrep.id, context: { lookaheadHours: DEFAULT_LOOKAHEAD_HOURS } } })
    }
  }

  const handlePeriodValueChanged = (newValue: number) => {
    setNewPeriodValue(normalizePeriodValue(newValue, periodType))
  }

  const handlePeriodTypeChanged = (newValue: PeriodType) => {
    const convertedValue = newValue === 'days' ? lookaheadHours / 24 : lookaheadHours
    setNewPeriodType(newValue)
    setNewPeriodValue(normalizePeriodValue(convertedValue, newValue))
  }

  const handleSave = async () => {
    if (!autoMeetingPrepWorkflow) {
      return
    }
    await updateEnabledWorkflow({ accountId, enabledWorkflowId: autoMeetingPrepWorkflow.id, payload: { context: { lookaheadHours } } })
  }

  const handleCancel = async () => {
    if (!autoMeetingPrepWorkflow) {
      return
    }
    setNewPeriodType(null)
    setNewPeriodValue(null)
  }

  return (
    <div className='d-flex flex-column w-100'>
      {enterpriseEnforced ? <EnterpriseManagedBanner className='zocks-mb-2' message={enterpriseManagedMessage} /> : null}
      <div className='d-flex align-items-center justify-content-between zocks-mb-2'>
        <div className='d-flex'>
          <ScopedBlock
            scope={{
              scope: 'enabled_workflow:update',
              superScope: ['enabled_workflow:admin'],
            }}
            missingScopeAction={MissingScopeAction.DISABLE}
          >
            <Form.Switch
              disabled={!autoMeetingPrepQuotaEnabled || isLoading || enterpriseEnforced || workflowSettingsError}
              checked={!!autoMeetingPrepWorkflow}
              onChange={(e) => handleToggleAutoMeetingPrep(e.currentTarget.checked)}
            />
          </ScopedBlock>
          <span className='ms-2'>Automatic Meeting Prep generation</span>
        </div>
        <ZocksAIAssistantTagVertical />
      </div>
      {autoMeetingPrepQuotaEnabled && autoMeetingPrepWorkflow ? (
        <div className='d-flex zocks-mb-2 justify-content-between'>
          <div className='d-flex zocks-gap-0.625 align-items-center'>
            <span>Prepare meetings</span>
            <Form.Control
              className='auto-meeting-prep-period-value zocks-border-color-gray-400'
              type='number'
              disabled={isLoading}
              value={periodValue}
              onChange={(e) => handlePeriodValueChanged(Number(e.currentTarget.value))}
              max={PERIOD_OPTIONS[periodType].max}
              min={1}
              step={1}
            />
            <div>
              <SelectDropdown
                options={Object.values(PERIOD_OPTIONS)}
                initialSelectedOption={Object.values(PERIOD_OPTIONS).find((o) => o.id === periodType)}
                onOptionSelected={(e) => handlePeriodTypeChanged(e.id as PeriodType)}
              />
            </div>
            <span>in advance</span>
          </div>
          <div className='d-flex gap-2'>
            <Button variant='secondary' disabled={!lookAheadHoursChanged} onClick={handleCancel}>
              Cancel
            </Button>
            <Button variant='primary' disabled={!lookAheadHoursChanged} onClick={handleSave}>
              Save
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
